import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  Bookmark,
  Check,
  CheckCircle2,
  ChevronRight,
  Compass,
  Eye,
  Globe2,
  HelpCircle,
  Lock,
  LogOut,
  Palette,
  RadioTower,
  RotateCcw,
  Search,
  Settings,
  SlidersHorizontal,
  UserPlus,
  UserRound,
  X,
} from "lucide-react";
import LogoutModal from "./LogoutModal";
import SupportDialog from "./SupportDialog";
import { logoutAllDevices, type User } from "../api/profile";

export const DRAWER_CONFIG_KEY = "blackspace.drawer.v1";

export type BlackspaceMenuItemId =
  | "profile"
  | "search"
  | "discover"
  | "saved"
  | "pulse"
  | "alerts"
  | "appearance"
  | "settings"
  | "sentinel"
  | "signout";

export type BlackspaceMenuConfigItem = {
  id: BlackspaceMenuItemId;
  visible: boolean;
};

type MenuAction = {
  id: BlackspaceMenuItemId;
  label: string;
  section: "go" | "system";
  icon: React.ElementType;
  tone: string;
  path?: string;
  onClick?: () => void;
  badge?: number;
  trailing?: React.ReactNode;
  locked?: boolean;
};

type BlackspaceMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  variant: "drawer" | "sheet";
  profile?: User | null;
  unreadCount?: number;
  canSeeSentinel?: boolean;
  isPresenceIdentity?: boolean;
  currentTheme?: string;
  onToggleTheme?: () => void;
  // Kept for existing callers; creation is intentionally not exposed in this menu.
  onCreatePost?: () => void;
  onCreateNote?: () => void;
  onCreateSpace?: () => void;
};

const DEFAULT_MENU_CONFIG: BlackspaceMenuConfigItem[] = [
  { id: "appearance", visible: true },
  { id: "settings", visible: true },
  { id: "signout", visible: true },
];

const NAV_MENU_IDS: BlackspaceMenuItemId[] = ["profile", "search", "discover", "saved", "pulse", "alerts"];

const LEGACY_ID_MAP: Partial<Record<string, BlackspaceMenuItemId>> = {
  profile: "profile",
  search: "search",
  discover: "discover",
  saved: "saved",
  pulse: "pulse",
  alerts: "alerts",
  appearance: "appearance",
  sentinel: "sentinel",
  signout: "signout",
};

function cleanUsername(value?: string | null) {
  return String(value || "").trim().replace(/^@+/, "");
}

function profileRoute(profile?: User | null) {
  const username = cleanUsername(profile?.username);
  return username ? `/user/${username}` : "/settings";
}

function formatHandle(value?: string | null) {
  const clean = cleanUsername(value);
  return clean ? `@${clean}` : "Identity active";
}

function safeNumber(value: unknown, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function compactCount(value: unknown) {
  const count = safeNumber(value);
  if (count >= 1000000) return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}K`;
  return String(count);
}

function displayTheme(value?: string | null) {
  const theme = String(value || "").trim();
  if (!theme || theme.toLowerCase() === "amoled") return "Dark";
  return theme.slice(0, 1).toUpperCase() + theme.slice(1).toLowerCase();
}

function displayLanguage(value: unknown) {
  const labels: Record<string, string> = {
    EN: "English", HI: "Hindi", FR: "French", IT: "Italian",
    RU: "Russian", ZA: "South Africa", AR: "Arabic",
  };
  return labels[String(value || "EN").toUpperCase()] || "English";
}

function isPresenceProfile(profile?: User | null, explicit = false) {
  const username = cleanUsername(profile?.username).toLowerCase();
  return Boolean(
    explicit ||
    profile?.isPresence ||
    profile?.realm?.isPresence ||
    profile?.realm?.role === "presence" ||
    profile?.realmRole === "presence" ||
    profile?.role === "presence" ||
    username === "blackspace"
  );
}

export function normalizeMenuConfig(input: unknown, canSeeSentinel = false): BlackspaceMenuConfigItem[] {
  const incoming = Array.isArray(input) ? input : [];
  const defaults = canSeeSentinel
    ? [...DEFAULT_MENU_CONFIG.slice(0, -1), { id: "sentinel" as const, visible: true }, DEFAULT_MENU_CONFIG[DEFAULT_MENU_CONFIG.length - 1]]
    : DEFAULT_MENU_CONFIG;
  const allowedIds = new Set(defaults.map((item) => item.id));
  const seen = new Set<string>();
  const ordered: BlackspaceMenuConfigItem[] = [];

  incoming.forEach((item: any) => {
    const mappedId = item?.id ? LEGACY_ID_MAP[String(item.id)] || item.id : "";
    if (typeof mappedId === "string" && allowedIds.has(mappedId as BlackspaceMenuItemId) && !seen.has(mappedId)) {
      seen.add(mappedId);
      ordered.push({ id: mappedId as BlackspaceMenuItemId, visible: item.visible !== false });
    }
  });

  defaults.forEach((item) => {
    if (!seen.has(item.id)) ordered.push(item);
  });
  return ordered;
}

function readLocalMenuConfig(canSeeSentinel = false): BlackspaceMenuConfigItem[] {
  try {
    return normalizeMenuConfig(JSON.parse(localStorage.getItem(DRAWER_CONFIG_KEY) || "[]"), canSeeSentinel);
  } catch {
    return normalizeMenuConfig([], canSeeSentinel);
  }
}

function clearLocalSession(keepTheme = true) {
  const savedTheme = keepTheme ? localStorage.getItem("theme") : null;
  localStorage.clear();
  sessionStorage.clear();
  if (savedTheme) localStorage.setItem("theme", savedTheme);
}

export default function BlackspaceMenu({
  isOpen,
  onClose,
  variant,
  profile,
  unreadCount = 0,
  canSeeSentinel = false,
  isPresenceIdentity = false,
  currentTheme = localStorage.getItem("theme") || "AMOLED",
  onToggleTheme,
}: BlackspaceMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportNotice, setSupportNotice] = useState(false);
  const [inviteNotice, setInviteNotice] = useState<{ text: string; url?: string } | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [config, setConfig] = useState<BlackspaceMenuConfigItem[]>(() => {
    const settingsConfig = profile?.settings?.drawerConfig || profile?.settings?.menuConfig;
    return normalizeMenuConfig(settingsConfig || readLocalMenuConfig(canSeeSentinel), canSeeSentinel);
  });

  useEffect(() => {
    setConfig((current) => {
      const settingsConfig = profile?.settings?.drawerConfig || profile?.settings?.menuConfig;
      return normalizeMenuConfig(settingsConfig || current, canSeeSentinel);
    });
  }, [canSeeSentinel, profile?.settings]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => closeButtonRef.current?.focus(), 30);
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!inviteNotice) return;
    const timeout = window.setTimeout(() => setInviteNotice(null), 6000);
    return () => window.clearTimeout(timeout);
  }, [inviteNotice]);

  useEffect(() => {
    if (!supportNotice) return;
    const timeout = window.setTimeout(() => setSupportNotice(false), 8000);
    return () => window.clearTimeout(timeout);
  }, [supportNotice]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (supportOpen) return;
        if (customizeOpen) {
          setCustomizeOpen(false);
          return;
        }
        if (logoutOpen) {
          setLogoutOpen(false);
          return;
        }
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [customizeOpen, isOpen, logoutOpen, onClose, supportOpen]);

  useEffect(() => {
    const normalized = normalizeMenuConfig(config, canSeeSentinel);
    localStorage.setItem(DRAWER_CONFIG_KEY, JSON.stringify(normalized));
  }, [canSeeSentinel, config]);

  const effectivePresenceIdentity = isPresenceProfile(profile, isPresenceIdentity);
  const name = profile?.displayName || profile?.username || "Blackspace";
  const handle = formatHandle(profile?.username);
  const initial = (name || "B").slice(0, 1).toUpperCase();
  const bio = String(profile?.bio || "").trim();
  const friendsCount = safeNumber(profile?.friendsCount ?? (Array.isArray(profile?.friends) ? profile?.friends.length : 0));
  const followersCount = safeNumber(profile?.followersCount ?? profile?.followerCount ?? (Array.isArray(profile?.followers) ? profile?.followers.length : friendsCount), friendsCount);
  const followingCount = safeNumber(profile?.followingCount ?? (Array.isArray(profile?.following) ? profile?.following.length : 0));
  const currentRoute = `${location.pathname}${location.search}${location.hash}`;

  const goTo = useCallback((path: string) => {
    onClose();
    navigate(path, { state: { from: currentRoute, label: "Previous page", reopenBlackspaceMenuOnBack: true } });
  }, [currentRoute, navigate, onClose]);

  const inviteFriends = useCallback(async () => {
    const origin = window.location.origin;
    const shareData = {
      title: "Blackspace",
      text: "Join me on Blackspace.",
      url: origin,
    };
    setInviteNotice(null);
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setInviteNotice({ text: "Invite shared." });
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(origin);
      setInviteNotice({ text: "Invite link copied." });
    } catch {
      setInviteNotice({ text: "Copy this invite link:", url: origin });
    }
  }, []);

  const handleLogout = async (allDevices: boolean) => {
    try {
      if (allDevices) await logoutAllDevices();
    } finally {
      clearLocalSession(true);
      window.location.replace("/login");
    }
  };

  const actions: Record<BlackspaceMenuItemId, MenuAction> = useMemo(() => ({
    profile: { id: "profile", label: "Profile", section: "go", icon: UserRound, tone: "text-cyan-200", path: profileRoute(profile) },
    search: { id: "search", label: "Search", section: "go", icon: Search, tone: "text-sky-200", path: "/search" },
    discover: { id: "discover", label: "Discover", section: "go", icon: Compass, tone: "text-violet-200", path: "/discover" },
    saved: { id: "saved", label: "Saved", section: "go", icon: Bookmark, tone: "text-amber-200", path: "/saved" },
    pulse: { id: "pulse", label: "Pulse", section: "go", icon: RadioTower, tone: "text-fuchsia-200", path: "/pulse" },
    alerts: { id: "alerts", label: "Alerts", section: "go", icon: Bell, tone: "text-emerald-200", path: "/alerts", badge: unreadCount },
    appearance: {
      id: "appearance",
      label: "Appearance",
      section: "system",
      icon: Palette,
      tone: "text-yellow-200",
      onClick: onToggleTheme || (() => goTo("/settings")),
      trailing: <span className="text-[11px] font-black uppercase tracking-[0.14em] text-neutral-500">{currentTheme}</span>,
    },
    settings: { id: "settings", label: "Settings", section: "system", icon: Settings, tone: "text-neutral-200", path: "/settings" },
    sentinel: { id: "sentinel", label: "Sentinel", section: "system", icon: Eye, tone: "text-cyan-100", path: "/sentinel", locked: !canSeeSentinel },
    signout: {
      id: "signout",
      label: "Logout",
      section: "system",
      icon: LogOut,
      tone: "text-rose-200",
      onClick: () => setLogoutOpen(true),
    },
  }), [canSeeSentinel, currentTheme, goTo, onToggleTheme, profile, unreadCount]);

  const accountActions = normalizeMenuConfig(config, canSeeSentinel)
    .filter((item) => item.visible)
    .map((item) => actions[item.id])
    .filter((item) => item && !item.locked);
  const navActions = variant === "sheet"
    ? NAV_MENU_IDS.map((id) => actions[id]).filter(Boolean)
    : [];
  const visibleActions = [...navActions, ...accountActions];

  const grouped = {
    go: visibleActions.filter((item) => item.section === "go"),
    system: visibleActions.filter((item) => item.section === "system"),
  };

  const isActive = (item: MenuAction) => {
    if (!item.path) return false;
    return location.pathname === item.path;
  };

  const runItem = (item: MenuAction) => {
    if (item.path) {
      goTo(item.path);
      return;
    }
    item.onClick?.();
  };

  const sheetProfileBio = bio || "Nothingness lives on!";
  const sheetShortcuts = [
    { ...actions.profile, sheetColor: "#22d3ee" },
    { ...actions.search, sheetColor: "#38bdf8" },
    { ...actions.discover, sheetColor: "#a78bfa" },
    { ...actions.saved, sheetColor: "#facc15" },
    { ...actions.pulse, sheetColor: "#2dd4bf" },
    { ...actions.alerts, sheetColor: "#fb7185" },
    ...(canSeeSentinel ? [{ ...actions.sentinel, sheetColor: "#22d3ee" }] : []),
  ];
  const sheetAccountRows = [
    {
      id: "appearance",
      label: "Appearance",
      icon: Palette,
      tone: "text-violet-200",
      trailing: displayTheme(currentTheme),
      onClick: () => goTo("/settings#settings-appearance-themes"),
    },
    { id: "settings", label: "Settings", icon: Settings, tone: "text-neutral-100", onClick: () => goTo("/settings") },
    ...(!effectivePresenceIdentity ? [{ id: "privacy", label: "Privacy", icon: Lock, tone: "text-neutral-100", onClick: () => goTo("/settings#settings-privacy") }] : []),
    { id: "notifications", label: "Notifications", icon: Bell, tone: "text-neutral-100", onClick: () => goTo("/settings#settings-notifications") },
    {
      id: "language",
      label: "Language",
      icon: Globe2,
      tone: "text-neutral-100",
      trailing: displayLanguage(profile?.settings?.language),
      onClick: () => goTo("/settings#settings-appearance-languages"),
    },
  ];
  const sheetToolRows = [
    { id: "invite", label: "Invite friends", icon: UserPlus, tone: "text-neutral-100", onClick: inviteFriends },
    { id: "support", label: "Help & Support", icon: HelpCircle, tone: "text-neutral-100", onClick: () => { setSupportNotice(false); setSupportOpen(true); } },
    { id: "report", label: "Report a problem", icon: AlertTriangle, tone: "text-rose-300", onClick: () => { setSupportNotice(false); setSupportOpen(true); } },
  ];

  const renderSheetRow = (item: {
    id: string;
    label: string;
    icon: React.ElementType;
    tone: string;
    onClick: () => void;
    trailing?: React.ReactNode;
  }) => {
    const Icon = item.icon;
    return (
      <button key={item.id} type="button" onClick={item.onClick} className="bs-menu-sheet-row">
        <span className={`bs-menu-sheet-row-icon ${item.tone}`}>
          <Icon size={18} strokeWidth={2.2} />
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-black text-neutral-100">{item.label}</span>
        {item.trailing ? <span className="bs-menu-sheet-row-trailing">{item.trailing}</span> : null}
        <ChevronRight size={16} className="shrink-0 text-neutral-500" />
      </button>
    );
  };

  const toggleConfigItem = (id: BlackspaceMenuItemId) => {
    setConfig((current) =>
      normalizeMenuConfig(current, canSeeSentinel).map((item) =>
        item.id === id ? { ...item, visible: !item.visible } : item
      )
    );
  };

  const resetConfig = () => {
    setConfig(normalizeMenuConfig([], canSeeSentinel));
  };

  const shellClass = variant === "drawer"
    ? `fixed inset-y-0 left-0 z-[1810] flex w-full flex-col overflow-hidden bg-[#030505]/96 shadow-[18px_0_90px_rgba(0,0,0,0.78)] backdrop-blur-2xl transition-transform duration-200 ease-out sm:w-[min(360px,calc(100vw-24px))] ${isOpen ? "translate-x-0" : "-translate-x-full"}`
    : `bs-mobile-command-sheet fixed inset-x-3 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-[2210] flex max-h-[76vh] flex-col overflow-hidden rounded-[1.35rem] bg-[#030505]/94 text-white shadow-[0_0_86px_rgba(8,145,178,0.24)] backdrop-blur-2xl transition duration-200 md:hidden ${isOpen ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0 pointer-events-none"}`;

  const Section = ({ title, items }: { title: string; items: MenuAction[] }) => {
    if (!items.length) return null;
    return (
      <div className="relative">
        <p className="mb-2 px-1 text-[10px] font-black uppercase tracking-[0.22em] text-neutral-600">{title}</p>
        <div className={variant === "sheet" && title !== "Account" ? "grid grid-cols-2 gap-2" : "space-y-1.5"}>
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => runItem(item)}
                className={[
                  "bs-blackspace-menu-item group relative overflow-hidden rounded-[1rem] px-3 py-3 text-left transition",
                  variant === "sheet" && title !== "Account" ? "min-h-[4.35rem]" : "flex w-full items-center gap-3",
                  active ? "is-active bg-white/[0.055] text-white" : "bg-white/[0.018] text-neutral-200 hover:bg-white/[0.045]",
                ].join(" ")}
              >
                <span className="pointer-events-none absolute inset-0 opacity-0 transition group-hover:opacity-100 bg-[linear-gradient(115deg,transparent_0%,rgba(34,211,238,0.08)_45%,transparent_62%)]" />
                <span className={`relative inline-grid h-8 w-8 place-items-center rounded-xl ${item.tone}`}>
                  <Icon size={17} />
                </span>
                <span className={variant === "sheet" && title !== "Account" ? "relative mt-2 flex items-center gap-2" : "relative flex min-w-0 flex-1 items-center gap-2"}>
                  <span className="truncate text-sm font-black">{item.label}</span>
                  {item.badge ? (
                    <span className="grid min-h-5 min-w-5 place-items-center rounded-md bg-cyan-300 px-1 text-[10px] font-black text-black">
                      {item.badge > 9 ? "9+" : item.badge}
                    </span>
                  ) : null}
                </span>
                {item.trailing ? <span className="relative ml-auto">{item.trailing}</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const menuContent = (
    <>
      {isOpen && (
        <button
          type="button"
          className={`fixed bg-black/78 backdrop-blur-md ${variant === "drawer" ? "inset-0 z-[1800]" : "inset-x-0 top-0 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-[2200] md:hidden"}`}
          aria-label="Close Blackspace menu"
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerUp={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }}
        />
      )}

      <aside
        className={shellClass}
        role="dialog"
        aria-modal="true"
        aria-label="Blackspace menu"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(34,211,238,0.14),transparent_34%),radial-gradient(circle_at_100%_0%,rgba(168,85,247,0.12),transparent_32%),linear-gradient(135deg,rgba(34,211,238,0.08),transparent_34%,rgba(236,72,153,0.06))]" />
          <span className="bs-settings-card-line absolute left-[-10%] top-[22%] h-px w-[125%] rotate-[-8deg] bg-cyan-300/18" />
          <span className="absolute bottom-0 left-0 h-px w-full bg-cyan-300/10" />
        </div>

        {supportNotice && (
          <div role="status" className="relative mx-3 mt-3 flex shrink-0 items-start gap-3 rounded-xl border border-emerald-300/30 bg-[linear-gradient(120deg,rgba(52,211,153,.14),rgba(34,211,238,.07))] px-3 py-3 text-emerald-50 shadow-[0_0_28px_rgba(52,211,153,.09)]">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-300" />
            <span className="min-w-0 text-xs leading-5"><strong className="block font-semibold">Blackspace has noted it.</strong><span className="text-slate-300">Help will arrive soon.</span></span>
          </div>
        )}

        {variant === "sheet" ? (
          <div className="bs-menu-sheet-premium relative min-h-0 flex-1 overflow-y-auto">
            <header className="bs-menu-sheet-profile">
              <button type="button" onClick={() => goTo(profileRoute(profile))} className="bs-menu-sheet-avatar" aria-label="Open profile">
                {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : <span>{initial}</span>}
                <span className="bs-menu-sheet-presence-dot" />
              </button>
              <button type="button" onClick={() => goTo(profileRoute(profile))} className="min-w-0 flex-1 text-left">
                <p className="truncate text-[14px] font-black text-white">{name}</p>
                <p className="mt-0.5 truncate text-[11px] font-semibold text-neutral-500">{handle}</p>
                <p className="mt-1 truncate text-[11px] font-semibold text-neutral-300/85">{sheetProfileBio}</p>
              </button>
              <button type="button" onClick={() => goTo("/settings")} className="bs-menu-sheet-gear" aria-label="Open settings">
                <Settings size={18} />
              </button>
            </header>

            <div className="bs-menu-sheet-shortcuts" aria-label="Quick navigation">
              {sheetShortcuts.map((item) => {
                const Icon = item.icon;
                return (
                  <button key={item.id} type="button" onClick={() => runItem(item)} className={`bs-menu-sheet-shortcut ${isActive(item) ? "is-active" : ""}`}>
                    <span className="bs-menu-sheet-shortcut-icon" style={{ color: item.sheetColor }}>
                      <Icon size={18} strokeWidth={2.25} />
                      {item.badge ? <span className="bs-menu-sheet-badge">{item.badge > 9 ? "9+" : item.badge}</span> : null}
                    </span>
                    <span className="bs-menu-sheet-shortcut-label">{item.label}</span>
                  </button>
                );
              })}
            </div>

            <section className="bs-menu-sheet-section" aria-label="Account">
              <p className="bs-menu-sheet-section-title">Account</p>
              <div className="bs-menu-sheet-row-group">
                {sheetAccountRows.map(renderSheetRow)}
              </div>
            </section>

            <section className="bs-menu-sheet-section" aria-label="Tools">
              <p className="bs-menu-sheet-section-title">Tools</p>
              <div className="bs-menu-sheet-row-group">
                {sheetToolRows.map(renderSheetRow)}
              </div>
              {inviteNotice && (
                <p role="status" className="mx-2 mt-2 rounded-xl border border-cyan-300/20 bg-cyan-300/[0.07] px-3 py-2 text-xs font-medium text-cyan-100">
                  {inviteNotice.text}
                  {inviteNotice.url && <span className="mt-1 block break-all select-all text-white">{inviteNotice.url}</span>}
                </p>
              )}
            </section>

            <button type="button" onClick={() => runItem(actions.signout)} className="bs-menu-sheet-logout">
              <LogOut size={17} />
              <span>Logout</span>
            </button>
          </div>
        ) : (
          <>
            <div className="relative flex shrink-0 items-start gap-3 px-4 pb-3 pt-4">
              <button
                type="button"
                onClick={() => goTo(profileRoute(profile))}
                className="grid h-12 w-12 shrink-0 place-items-center rounded-[1rem] bg-black/45 text-lg font-black text-cyan-100 shadow-[0_0_32px_rgba(34,211,238,0.12)]"
              >
                {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" className="h-full w-full rounded-[1rem] object-cover" /> : initial}
              </button>
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-black text-white">{name}</p>
                <p className="truncate text-xs font-semibold text-neutral-500">{handle}</p>
                {bio && <p className="mt-1 line-clamp-1 text-[11px] font-semibold text-neutral-600">{bio}</p>}
                {!effectivePresenceIdentity && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => goTo(profileRoute(profile))} className="min-w-0 rounded-xl border border-white/[0.06] bg-white/[0.025] px-3 py-2 text-left transition hover:border-cyan-300/20 hover:bg-cyan-300/10">
                      <span className="block text-sm font-black text-white">{compactCount(followersCount)}</span>
                      <span className="mt-0.5 block truncate text-[11px] font-semibold text-neutral-500">Followers</span>
                    </button>
                    <button type="button" onClick={() => goTo(profileRoute(profile))} className="min-w-0 rounded-xl border border-white/[0.06] bg-white/[0.025] px-3 py-2 text-left transition hover:border-cyan-300/20 hover:bg-cyan-300/10">
                      <span className="block text-sm font-black text-white">{compactCount(followingCount)}</span>
                      <span className="mt-0.5 block truncate text-[11px] font-semibold text-neutral-500">Following</span>
                    </button>
                  </div>
                )}
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="grid h-10 w-10 shrink-0 place-items-center text-neutral-500 transition hover:text-white"
                aria-label="Close Blackspace menu"
              >
                <X size={18} />
              </button>
            </div>

            <div className="stealth-scroll relative min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-4">
              <Section title="Account" items={grouped.system} />
            </div>

            <div className="relative shrink-0 border-t border-white/[0.055] bg-black/30 p-3">
              <button
                type="button"
                onClick={() => setCustomizeOpen(true)}
                className="flex w-full items-center gap-3 rounded-[1rem] bg-white/[0.018] px-3 py-3 text-left text-sm font-black text-neutral-200 transition hover:bg-white/[0.045] hover:text-white"
              >
                <SlidersHorizontal size={17} className="text-cyan-200" />
                Customize
              </button>
            </div>
          </>
        )}
      </aside>

      {customizeOpen && (
        <div
          className="fixed inset-0 z-[2650] flex items-end justify-center bg-black/72 px-3 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 backdrop-blur-md sm:items-center sm:p-4"
          onMouseDown={() => setCustomizeOpen(false)}
        >
          <div
            className="relative w-full max-w-md overflow-hidden rounded-[1.25rem] border border-cyan-300/15 bg-[#050607]/96 p-4 text-white shadow-[0_0_90px_rgba(34,211,238,0.18)]"
            role="dialog"
            aria-modal="true"
            aria-label="Customize menu"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className="truncate text-base font-black text-white">Customize</h2>
                <p className="mt-1 text-xs font-semibold text-neutral-500">Choose account shortcuts for this menu.</p>
              </div>
              <button type="button" onClick={() => setCustomizeOpen(false)} className="grid h-9 w-9 shrink-0 place-items-center text-neutral-500 transition hover:text-white" aria-label="Close customize menu">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-2">
              {normalizeMenuConfig(config, canSeeSentinel).map((item) => {
                const meta = actions[item.id];
                if (!meta || meta.locked) return null;
                const Icon = meta.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => toggleConfigItem(item.id)}
                    className="flex w-full items-center gap-3 rounded-[1rem] bg-white/[0.02] px-3 py-3 text-left transition hover:bg-white/[0.05]"
                  >
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-black/35 ${meta.tone}`}>
                      <Icon size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-black text-neutral-100">{meta.label}</span>
                      <span className="block text-[11px] font-semibold text-neutral-600">{item.visible ? "Shown" : "Hidden"}</span>
                    </span>
                    <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-xl border ${item.visible ? "border-emerald-300/20 bg-emerald-300/10 text-emerald-200" : "border-white/10 bg-white/[0.02] text-neutral-600"}`}>
                      {item.visible ? <Check size={15} /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-white/[0.055] pt-4">
              <button type="button" onClick={resetConfig} className="inline-flex items-center gap-2 rounded-xl bg-white/[0.025] px-4 py-2 text-sm font-black text-neutral-300 transition hover:bg-white/[0.055] hover:text-white">
                <RotateCcw size={15} />
                Reset
              </button>
              <button type="button" onClick={() => setCustomizeOpen(false)} className="inline-flex items-center gap-2 rounded-xl bg-cyan-300 px-5 py-2 text-sm font-black text-black shadow-[0_0_34px_rgba(34,211,238,0.16)] transition hover:bg-cyan-200">
                <Check size={15} />
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <LogoutModal isOpen={logoutOpen} onConfirm={handleLogout} onCancel={() => setLogoutOpen(false)} />
      <SupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} username={profile?.username} onCreated={() => { setSupportOpen(false); setSupportNotice(true); }} />
    </>
  );

  return typeof document === "undefined" ? menuContent : createPortal(menuContent, document.body);
}
